import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import {
  AutocompleteInputComponent,
  AutocompleteItem,
} from '@delmonicos/shared-design-system-react/src/components/autocomplete-input/autocomplete-input.component';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { CountryCode } from '@delmonicos/shared-design-system-react/src/components/country-code/country-code';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import { GeoMap } from '@delmonicos/shared-design-system-react/src/components/location/geo-map/geo-map.component';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { getTimezoneFromCoordinates } from '@delmonicos/shared-sdk/libs/core/src/helpers/get-timezone-from-coordinates';
import { useDebounce } from '@delmonicos/shared-sdk/libs/core/src/hooks';
import { useCreateLocationFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/create-location.form-validation';
import { CreateLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/create-location.model';
import { useEffect, useState } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ConfigurationService } from '@app/core/configuration/configuration.service';
import { useCustomerAutocomplete } from '@app/feature/customer/use-customer-autocomplete';
import {
  LocationMapFieldPaths,
  LocationMapFormSync,
  useLocationMapSync,
} from '@app/feature/location/use-location-map-sync';
import { useCpmsBoGetNetworksPaginatedQuery } from '@service/cpms-bo-network.service';

import * as css from './location-create-form-step-2.module.scss';

const CREATE_LOCATION_MAP_FIELD_PATHS: LocationMapFieldPaths<CreateLocationModel> =
  {
    address: 'address',
    city: 'city',
    postalCode: 'postalCode',
    country: 'country',
    latitude: 'coordinates.latitude',
    longitude: 'coordinates.longitude',
  };

// Derives `timeZone` from the coordinate fields without rendering — keeps
// this out of LocationCreateFormStep2LocalizationFields so a lat/lng
// keystroke doesn't re-render every sibling field Controller (mirrors
// LocationMapFormSync below). Kept independent from that map sync — rather
// than merged into it — because it must keep working when no Google Maps
// API key is configured and LocationMapFormSync isn't mounted.
const LocationCreateFormStep2TimezoneSync = (): null => {
  const { watch, getValues, setValue } = useFormContext<CreateLocationModel>();

  const debouncedLatitude = useDebounce(watch('coordinates.latitude'), 600);
  const debouncedLongitude = useDebounce(watch('coordinates.longitude'), 600);

  useEffect(() => {
    const detectedTimeZone = getTimezoneFromCoordinates(
      debouncedLatitude,
      debouncedLongitude,
    );

    if (detectedTimeZone !== getValues('timeZone')) {
      setValue('timeZone', detectedTimeZone, { shouldDirty: true });
    }
  }, [debouncedLatitude, debouncedLongitude, getValues, setValue]);

  return null;
};

// Pure form fields — no map context dependency
const LocationCreateFormStep2LocalizationFields = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { control } = useFormContext<CreateLocationModel>();
  const { address, city, postalCode, country, latitude, longitude, timeZone } =
    useCreateLocationFormValidation();

  return (
    <Stack flex={1} gap={GapEnum.M} direction="column">
      <Controller
        name="address"
        control={control}
        rules={address}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-location.address')}
            placeholder={t('delmonicos-shared-sdk.outputs-location.address')}
            required={!!address.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? '' : e.target.value)
            }
          />
        )}
      />

      <Stack
        gap={GapEnum.M}
        flexWrap="wrap"
        className={css.countryCityContainer}>
        <Controller
          name="city"
          control={control}
          rules={city}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-location.city')}
              placeholder={t('delmonicos-shared-sdk.outputs-location.city')}
              required={!!city.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
        <Controller
          name="postalCode"
          control={control}
          rules={postalCode}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-location.postal-code')}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-location.postal-code',
              )}
              required={!!postalCode.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
      </Stack>

      <Controller
        name="country"
        control={control}
        rules={country}
        render={({ field, fieldState: { error } }) => (
          <CountryCode
            errorMessage={error?.message}
            fieldValue={field.value}
            fieldOnChange={field.onChange}
          />
        )}
      />

      <Stack gap={GapEnum.S} flexWrap="wrap">
        <Controller
          name="coordinates.latitude"
          control={control}
          rules={latitude}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-location.latitude')}
              placeholder={t('delmonicos-shared-sdk.outputs-location.latitude')}
              required={!!latitude.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
        <Controller
          name="coordinates.longitude"
          control={control}
          rules={longitude}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-location.longitude')}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-location.longitude',
              )}
              required={!!longitude.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : e.target.value)
              }
            />
          )}
        />
      </Stack>

      <Controller
        name="timeZone"
        control={control}
        rules={timeZone}
        render={({ field }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-location.time-zone')}
            placeholder={t(
              '$.location-create.step-2.localization.timezone-not-detected',
            )}
            readOnly
            {...field}
            value={field.value ?? ''}
          />
        )}
      />
    </Stack>
  );
};

const LocationCreateFormStep2LocalizationFragment = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // HANDLERS — map → form / form → map //////////////////////////////////////

  const { addressGuard, coordinatesGuard, handleAddressChange } =
    useLocationMapSync(CREATE_LOCATION_MAP_FIELD_PATHS);

  // RENDER ////////////////////////////////////////////////////////////////////

  const apiKey = ConfigurationService.getGoogleApiKey();

  const fieldsStack = (
    <Stack gap={GapEnum.M} flex={1} direction="column">
      <AccentTitle
        icon="marker-pin-04"
        text={t('$.location-update.step-2.localization.title')}
        as={HeadingEnum.H2}
      />
      <LocationCreateFormStep2LocalizationFields />
      <LocationCreateFormStep2TimezoneSync />
    </Stack>
  );

  if (!apiKey) {
    return (
      <Card gap={GapEnum.L} flex={1}>
        {fieldsStack}
      </Card>
    );
  }

  return (
    <Card gap={GapEnum.L} flex={1} direction="row">
      {fieldsStack}
      <Stack flex={1} className={css.mapContainer}>
        <GeoMap
          interactive
          apiKey={apiKey}
          mapId="create-location-map"
          zoom={16}
          onAddressChange={handleAddressChange}>
          <LocationMapFormSync
            fieldPaths={CREATE_LOCATION_MAP_FIELD_PATHS}
            addressGuard={addressGuard}
            coordinatesGuard={coordinatesGuard}
          />
        </GeoMap>
      </Stack>
    </Card>
  );
};

const LocationCreateFormStep1CustomerSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<CreateLocationModel, 'customerId'>;
  fieldState: ControllerFieldState;
}): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { customerId } = useCreateLocationFormValidation();

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { search, setSearch, selectedItem, autocompleteItems } =
    useCustomerAutocomplete(field.value);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-network.customer')}
      placeholder={t('delmonicos-shared-sdk.outputs-network.customer')}
      required={!!customerId.required}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(selectedItemNext?.key ?? '');
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

const LocationCreateFormStep1NetworkSelection = ({
  field,
  fieldState: { error },
}: {
  field: ControllerRenderProps<CreateLocationModel, 'networkId'>;
  fieldState: ControllerFieldState;
}): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { watch } = useFormContext<CreateLocationModel>();
  const { networkId } = useCreateLocationFormValidation();

  // WATCHERS ////////////////////////////////////////////////////////////////

  const customerId = watch('customerId');

  useEffect(() => {
    if (field.value && !customerId) {
      field.onChange('');
    }
  }, [customerId, field]);

  // SEARCH TERM ///////////////////////////////////////////////////////////////

  const [search, setSearch] = useState<string>('');

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { data, isSuccess } = useCpmsBoGetNetworksPaginatedQuery({
    limit: 10,
    offset: 0,
    customerId,
    name: search === '' ? undefined : `~${search}`,
  });

  // AUTOCOMPLETE ITEM /////////////////////////////////////////////////////////

  const [selectedItem, setSelectedItem] = useState<AutocompleteItem | null>(
    null,
  );

  useEffect(() => {
    if (isSuccess) {
      const network = data.docs.find(({ id }) => id === field.value) ?? null;

      if (network) {
        setSelectedItem({ key: network.id, label: network.name });
        return;
      }

      setSelectedItem(null);
    }
  }, [search, data, isSuccess, field.value]);

  // AUTOCOMPLETE ITEMS ////////////////////////////////////////////////////////

  const [autocompleteItems, setAutocompleteItems] = useState<
    AutocompleteItem[]
  >([]);

  useEffect(() => {
    if (isSuccess) {
      setAutocompleteItems(
        data.docs.map(({ id, name }) => ({ key: id, label: name })) ?? [],
      );
    }
  }, [search, data, isSuccess]);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <AutocompleteInputComponent
      label={t('delmonicos-shared-sdk.outputs-location.network')}
      placeholder={t('delmonicos-shared-sdk.outputs-location.network')}
      required={!!networkId.required}
      disabled={field.disabled || !customerId}
      value={search}
      onChange={setSearch}
      onSearch={setSearch}
      items={autocompleteItems}
      selectedItem={selectedItem}
      onSelect={(selectedItemNext) => {
        field.onChange(selectedItemNext?.key ?? '');
      }}
      emptyResultText={t('global.no-result-found')}
      errorMessage={error?.message}
    />
  );
};

const LocationCreateFormStep2NetworkFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { control } = useFormContext<CreateLocationModel>();
  const { customerId, networkId } = useCreateLocationFormValidation();

  return (
    <Card flex={1} gap={GapEnum.M} direction="column">
      <AccentTitle
        icon="server-06"
        as={HeadingEnum.H2}
        text={t('delmonicos-shared-sdk.outputs-location.network')}
        variant={AccentVariantEnum.Secondary}
      />

      <Stack gap={GapEnum.M} flex={1} className={css.networkContainer}>
        <Controller
          name="customerId"
          control={control}
          rules={customerId}
          render={LocationCreateFormStep1CustomerSelection}
        />
        <Controller
          name="networkId"
          control={control}
          rules={networkId}
          render={LocationCreateFormStep1NetworkSelection}
        />
      </Stack>
    </Card>
  );
};

export const LocationCreateFormStep2Controller = (): React.JSX.Element => {
  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack gap={GapEnum.L} flex={1} direction="column">
      <Stack gap={GapEnum.L} direction="column" alignItems="stretch">
        <LocationCreateFormStep2NetworkFragment />
      </Stack>
      <Stack gap={GapEnum.L} direction="column" flex={1} alignItems="stretch">
        <LocationCreateFormStep2LocalizationFragment />
      </Stack>
    </Stack>
  );
};
